import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Redirect, Link } from 'react-router-dom';
import classNames from 'classnames';
import ClipLoader from 'react-spinners/ClipLoader';
import { toastr } from 'react-redux-toastr';

import { useFormatMessage, useFormatDate } from 'hooks';
import Table from 'components/Table';
import {
  fetchMerchants,
  deleteMerchant,
  merchantsCleanUp,
} from 'state/actions/merchants';
import paths from 'pages/Router/paths';
import ConfirmationModal from 'components/ConfirmationModal';
import classes from './Merchants.module.scss';

const Merchants = () => {
  const { merchantsList, isAdmin, error, loading, deleted } = useSelector(
    (state) => ({
      merchantsList: state.merchants.data,
      isAdmin: state.auth.userData.isAdmin,
      error: state.merchants.error,
      loading: state.merchants.loading,
      deleted: state.merchants.deleted,
    }),
    shallowEqual
  );

  const [deleteModal, setDeleteModal] = useState({
    merchantId: null,
    isOpen: false,
  });

  const dispatch = useDispatch();

  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchMerchants());
    }

    return () => dispatch(merchantsCleanUp());
  }, [isAdmin]);

  useEffect(() => {
    if (deleted && !loading) {
      setDeleteModal((prevState) => ({
        userId: null,
        isOpen: !prevState.isOpen,
      }));
    }
  }, [deleted, loading]);

  const redirect = !isAdmin && <Redirect to={paths.ROOT} />;

  const onRemoveButtonClickHandler = (userId) => {
    setDeleteModal((prevState) => ({
      userId,
      isOpen: !prevState.isOpen,
    }));
  };

  const onCloseModalHandler = () => {
    setDeleteModal({ userId: null, isOpen: false });
  };

  const onDeleteUserHandler = () => {
    dispatch(deleteMerchant(deleteModal.userId));
  };

  const SUCCESS_MESSAGE = useFormatMessage('Applications.clipboard');

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toastr.success('', SUCCESS_MESSAGE);
    } catch (err) {
      // eslint-disable-next-line
      console.error('Failed to copy: ', err);
    }
  };

  const columns = [
    {
      Header: '',
      accessor: 'logoUrl',
      Cell: ({ row }) => (
        <div className="image">
          <img src={row.original.logoUrl} alt="" />
        </div>
      ),
      disableSortBy: true,
    },
    {
      Header: useFormatMessage('Merchants.name'),
      accessor: 'name',
    },
    {
      Header: useFormatMessage('Merchants.url'),
      accessor: 'url',
      Cell: ({ row }) => (
        <small
          onClick={() => copyToClipboard(row?.original?.url)}
          style={{ cursor: 'pointer' }}
          className="is-abbr-like"
        >
          {row?.original?.url?.slice(0, 20)}...
        </small>
      ),
    },
    {
      Header: useFormatMessage('Merchants.active'),
      accessor: 'active',
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {row.original.active ? (
            <span className="icon">
              <i className="mdi mdi-check" />
            </span>
          ) : (
            <span className="icon">
              <i className="mdi mdi-close" />
            </span>
          )}
        </small>
      ),
    },
    {
      Header: useFormatMessage('Merchants.created'),
      accessor: 'created',
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {useFormatDate(row.original.createdAt, {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </small>
      ),
    },
    {
      Header: '',
      id: 'actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          {!row.original.isAdmin && (
            <div className="buttons is-right">
              <Link
                to={paths.APPLICATIONS.replace(':id', row.original.id)}
                className="button is-small is-info"
              >
                <span className="icon is-small">
                  <i className="mdi mdi-application" />
                </span>
              </Link>
              <Link
                to={`/merchants/${row.original.id}`}
                className="button is-small is-primary"
              >
                <span className="icon is-small">
                  <i className="mdi mdi-account-edit" />
                </span>
              </Link>

              <button
                type="button"
                className="button is-small is-danger"
                onClick={() => onRemoveButtonClickHandler(row.original.id)}
              >
                <span className="icon is-small">
                  <i className="mdi mdi-trash-can" />
                </span>
              </button>
            </div>
          )}
        </>
      ),
      disableSortBy: true,
    },
  ];

  const data = useMemo(
    () =>
      search
        ? merchantsList.filter((el) => {
            const clonedElem = { ...el };
            delete clonedElem.id;
            delete clonedElem.isAdmin;
            delete clonedElem.logoUrl;
            return Object.values(clonedElem).some((field) =>
              field
                .toString()
                .toLowerCase()
                .includes(search.toString().toLowerCase())
            );
          })
        : merchantsList,
    [search, merchantsList]
  );

  const deleteMessage = useFormatMessage('Merchants.delete');

  const confirmMessage = useFormatMessage('Merchants.confirm');

  const permDeleteMessage = useFormatMessage('Merchants.permDelete');

  const cancelMessage = useFormatMessage('Merchants.cancel');

  return (
    <>
      {redirect}
      {deleteModal.isOpen && (
        <ConfirmationModal
          isActive={deleteModal.isOpen}
          isLoading={loading}
          confirmButtonMessage={deleteMessage}
          title={confirmMessage}
          body={permDeleteMessage}
          cancelButtonMessage={cancelMessage}
          onConfirmation={onDeleteUserHandler}
          onCancel={onCloseModalHandler}
        />
      )}
      <section className="hero is-hero-bar">
        <div className="hero-body">
          <div className="level">
            <div className="level-left">
              <div className="level-item">
                <h1 className="title">
                  {useFormatMessage('Merchants.merchants')}
                </h1>
              </div>
            </div>
            <div className="level-right">
              <div className="level-item">
                <Link to={paths.ADD_MERCHANT} className="button">
                  {useFormatMessage('Merchants.newMerchant')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="section is-main-section">
        <div className="card has-table has-mobile-sort-spaced">
          <header className="card-header">
            <p className={classNames('card-header-title', classes.tableHeader)}>
              <span>{useFormatMessage('Merchants.search')}</span>
              <input
                type="text"
                className="input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </p>
          </header>
          <div className="b-table">
            {loading && data.lenght > 0 ? (
              <ClipLoader />
            ) : (
              <Table columns={columns} data={data} />
            )}
            {error && 'Show error'}
          </div>
        </div>
      </section>
    </>
  );
};

export default Merchants;
